import React, { useState } from 'react';
import { 
  Layout,
  BookOpen,
  FileText,
  Settings,
  Shield,
  Users,
  Eye,
  Tool,
  ArrowRight,
  ChevronDown,
  ChevronRight,
  CheckCircle,
  AlertTriangle,
  Edit,
  Save,
  Plus,
  Search,
  Monitor,
  Play,
  Database,
  Lock,
  AlertCircle,
  HelpCircle,
  Image,
  Activity
} from 'lucide-react';
// Structure de données améliorée pour la documentation fonctionnelle
const FUNCTIONAL_DOCUMENTATION = {
  metadata: {
    title: "Documentation Fonctionnelle",
    version: "3.0.0",
    lastUpdate: "2024-03-23",
    status: "published",
    author: "Équipe Produit",
    reviewedBy: "Direction Métier"
  },
  pages: [
    {
      id: "user-registration",
      title: "Inscription Utilisateur",
      description: "Page d'inscription permettant aux nouveaux utilisateurs de créer leur compte",
      category: "Gestion Utilisateurs",
      version: "2.1",
      status: "production",
      preConditions: {
        systemRequirements: [
          {
            type: "configuration",
            description: "Service d'envoi d'email configuré",
            critical: true
          },
          {
            type: "service",
            description: "API d'authentification disponible",
            critical: true
          }
        ],
        userRequirements: [
          {
            type: "access",
            description: "Accès public sans authentification",
          },
          {
            type: "browser",
            description: "Navigateur moderne avec JavaScript activé"
          }
        ],
        dataRequirements: [
          {
            type: "database",
            description: "Table utilisateurs accessible en écriture"
          }
        ]
      },
      interface: {
        layout: {
          type: "single-column",
          width: "max-w-md",
          centered: true,
          responsive: true
        },
        sections: [
          {
            id: "header",
            type: "header",
            title: "Création de compte",
            description: "Remplissez le formulaire pour créer votre compte"
          },
          {
            id: "form",
            type: "form",
            fields: [
              {
                name: "email",
                label: "Adresse email",
                type: "email",
                required: true,
                validation: {
                  pattern: "^[\\w-\\.]+@([\\w-]+\\.)+[\\w-]{2,4}$",
                  message: "Email invalide"
                }
              },
              {
                name: "password",
                label: "Mot de passe",
                type: "password",
                required: true,
                validation: {
                  minLength: 8,
                  requirements: [
                    "Une majuscule",
                    "Un chiffre",
                    "Un caractère spécial"
                  ]
                }
              },
              {
                name: "confirmPassword",
                label: "Confirmer le mot de passe",
                type: "password",
                required: true,
                validation: {
                  match: "password",
                  message: "Les mots de passe ne correspondent pas"
                }
              }
            ],
            actions: [
              {
                type: "submit",
                label: "S'inscrire",
                primary: true
              },
              {
                type: "link",
                label: "Déjà inscrit ?",
                href: "/login"
              }
            ]
          }
        ],
        states: [
          {
            id: "initial",
            description: "Formulaire vide"
          },
          {
            id: "validating",
            description: "Validation en cours"
          },
          {
            id: "submitting",
            description: "Envoi en cours"
          },
          {
            id: "success",
            description: "Inscription réussie"
          },
          {
            id: "error",
            description: "Erreur lors de l'inscription"
          }
        ]
      },
      processes: [
        {
          id: "form-validation",
          name: "Validation du formulaire",
          description: "Processus de validation des données saisies",
          steps: [
            {
              order: 1,
              description: "Validation format email",
              validation: "Expression régulière email"
            },
            {
              order: 2,
              description: "Validation mot de passe",
              validation: "Règles de complexité"
            },
            {
              order: 3,
              description: "Vérification correspondance mots de passe",
              validation: "Égalité stricte"
            }
          ]
        },
        {
          id: "account-creation",
          name: "Création du compte",
          description: "Processus de création du compte utilisateur",
          steps: [
            {
              order: 1,
              description: "Vérification unicité email",
              type: "check"
            },
            {
              order: 2,
              description: "Hashage mot de passe",
              type: "security"
            },
            {
              order: 3,
              description: "Création compte en base",
              type: "database"
            },
            {
              order: 4,
              description: "Envoi email confirmation",
              type: "notification"
            }
          ]
        }
      ],
      postConditions: {
        systemState: [
          {
            description: "Compte créé en base de données",
            table: "users",
            status: "inactive"
          },
          {
            description: "Email de confirmation envoyé",
            type: "notification"
          }
        ],
        userState: [
          {
            description: "Redirection page confirmation",
            url: "/register/confirm"
          }
        ],
        dataState: [
          {
            description: "Journal d'inscription créé",
            type: "audit"
          }
        ]
      },
      configuration: {
        parameters: [
          {
            key: "PASSWORD_MIN_LENGTH",
            type: "number",
            default: 8,
            description: "Longueur minimum du mot de passe"
          },
          {
            key: "EMAIL_CONFIRMATION_REQUIRED",
            type: "boolean",
            default: true,
            description: "Activation confirmation par email"
          }
        ],
        features: [
          {
            key: "SOCIAL_LOGIN",
            type: "boolean",
            default: false,
            description: "Activation connexion réseaux sociaux"
          }
        ]
      },
      documentation: {
        screenshots: [
          {
            title: "Formulaire vide",
            path: "/screenshots/register-empty.png",
            description: "État initial du formulaire"
          },
          {
            title: "Validation erreurs",
            path: "/screenshots/register-errors.png",
            description: "Affichage des erreurs de validation"
          }
        ],
        userGuides: [
          {
            title: "Guide inscription",
            path: "/guides/registration.pdf",
            description: "Guide détaillé du processus d'inscription"
          }
        ],
        testCases: [
          {
            id: "TC001",
            title: "Inscription réussie",
            steps: [
              "Remplir tous les champs correctement",
              "Soumettre le formulaire",
              "Vérifier la redirection"
            ]
          }
        ]
      }
    }
    // Autres pages...
  ]
};

// Composants UI de base
const Card = ({ children, className = '' }) => (
  <div className={`bg-white rounded-lg shadow-sm border border-gray-200 ${className}`}>
    {children}
  </div>
);

const CardHeader = ({ children, className = '' }) => (
  <div className={`px-6 py-4 border-b border-gray-200 ${className}`}>
    {children}
  </div>
);

const CardTitle = ({ children, className = '' }) => (
  <h3 className={`text-lg font-semibold text-gray-900 ${className}`}>
    {children}
  </h3>
);

const CardContent = ({ children, className = '' }) => (
  <div className={`px-6 py-4 ${className}`}>
    {children}
  </div>
);

const Button = ({ 
  children, 
  className = '', 
  variant = 'default', 
  size = 'default',
  onClick,
  ...props 
}) => {
  const baseStyles = 'inline-flex items-center justify-center rounded-md font-medium transition-colors';
  const variants = {
    default: 'bg-blue-500 text-white hover:bg-blue-600',
    outline: 'border border-gray-300 bg-white hover:bg-gray-100 text-gray-700',
    ghost: 'hover:bg-gray-100 text-gray-700'
  };
  const sizes = {
    default: 'h-10 px-4 py-2',
    sm: 'h-8 px-3 text-sm',
    lg: 'h-12 px-6'
  };

  return (
    <button
      className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`}
      onClick={onClick}
      {...props}
    >
      {children}
    </button>
  );
};
// Composant de visualisation des pré-conditions
const PreConditionsSection = ({ preConditions }) => {
  const [isExpanded, setIsExpanded] = useState(true);

  return (
    <Card className="mb-6">
      <CardHeader 
        className="cursor-pointer hover:bg-gray-50"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <Shield className="h-5 w-5 text-blue-500" />
            <div>
              <CardTitle>Pré-conditions</CardTitle>
              <p className="text-sm text-gray-600">Conditions requises pour l'utilisation</p>
            </div>
          </div>
          <ChevronDown 
            className={`h-5 w-5 transform transition-transform ${
              isExpanded ? '' : '-rotate-90'
            }`} 
          />
        </div>
      </CardHeader>

      {isExpanded && (
        <CardContent>
          <div className="space-y-6">
            {/* Exigences système */}
            {preConditions.systemRequirements && (
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">
                  Exigences Système
                </h4>
                <div className="space-y-2">
                  {preConditions.systemRequirements.map((req, index) => (
                    <div 
                      key={index} 
                      className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg"
                    >
                      <Settings className="h-5 w-5 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-700">{req.description}</p>
                        {req.critical && (
                          <span className="inline-flex items-center px-2 py-0.5 mt-1 rounded text-xs font-medium bg-red-100 text-red-800">
                            Critique
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Exigences utilisateur */}
            {/* Section similaire pour userRequirements */}

            {/* Exigences données */}
            {/* Section similaire pour dataRequirements */}
          </div>
        </CardContent>
      )}
    </Card>
  );
};

// Composant de visualisation de l'interface
const InterfaceSection = ({ interface: uiInterface }) => {
  const [activeTab, setActiveTab] = useState('layout');

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Layout className="h-5 w-5 text-blue-500" />
          <span>Interface Utilisateur</span>
        </CardTitle>
      </CardHeader>
      <div className="border-b border-gray-200">
        <nav className="flex -mb-px">
          {[
            { id: 'layout', label: 'Structure', icon: Layout },
            { id: 'components', label: 'Composants', icon: Monitor },
            { id: 'states', label: 'États', icon: Activity }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } flex items-center px-4 py-2 border-b-2 text-sm font-medium`}
            >
              <tab.icon className="h-4 w-4 mr-2" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>
      <CardContent>
        {activeTab === 'layout' && (
          <div className="space-y-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="text-sm font-medium text-gray-900 mb-2">
                Type de mise en page
              </h4>
              <p className="text-sm text-gray-600">{uiInterface.layout.type}</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {Object.entries(uiInterface.layout)
                  .filter(([key]) => key !== 'type')
                  .map(([key, value]) => (
                    <span
                      key={key}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                    >
                      {key}: {value.toString()}
                    </span>
                  ))}
              </div>
            </div>

            {/* Sections de l'interface */}
            <div className="space-y-4">
              {uiInterface.sections.map((section) => (
                <div 
                  key={section.id}
                  className="border rounded-lg p-4"
                >
                  <h4 className="font-medium text-gray-900 mb-2">
                    {section.title || section.id}
                  </h4>
                  {section.type === 'form' && section.fields && (
                    <div className="mt-3 space-y-3">
                      {section.fields.map((field) => (
                        <div
                          key={field.name}
                          className="flex items-start p-3 bg-gray-50 rounded"
                        >
                          <div>
                            <p className="text-sm font-medium text-gray-700">
                              {field.label}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              Type: {field.type}
                              {field.required && ' • Requis'}
                            </p>
                            {field.validation && (
                              <div className="mt-2">
                                <p className="text-xs font-medium text-gray-600">
                                  Validation:
                                </p>
                                <ul className="mt-1 list-disc list-inside text-xs text-gray-500">
                                  {Object.entries(field.validation).map(([key, value]) => (
                                    <li key={key}>
                                      {key}: {
                                        Array.isArray(value) 
                                          ? value.join(', ') 
                                          : value
                                      }
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'states' && (
          <div className="space-y-4">
            {uiInterface.states.map((state) => (
              <div
                key={state.id}
                className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg"
              >
                <div className="flex-1">
                  <h4 className="text-sm font-medium text-gray-900">
                    {state.id}
                  </h4>
                  <p className="mt-1 text-sm text-gray-600">
                    {state.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Composant principal de page
const PageDocumentation = ({ page }) => {
  return (
    <div className="space-y-6">
      {/* En-tête de la page */}
      <div className="bg-white px-6 py-4 shadow rounded-lg">
        <div className="flex justify-between items-start">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{page.title}</h2>
            <p className="mt-1 text-gray-600">{page.description}</p>
          </div>
          <div className="flex items-center space-x-2">
            <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
              v{page.version}
            </span>
            <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
              {page.status}
            </span>
          </div>
        </div>
      </div>

      {/* Sections principales */}
      <PreConditionsSection preConditions={page.preConditions} />
      <InterfaceSection interface={page.interface} />
      {/* Autres sections... */}
    </div>
  );
};

// Composant principal de documentation
const DocumentationFonctionnelle = () => {
  const [docData, setDocData] = useState(FUNCTIONAL_DOCUMENTATION);
  const [selectedPage, setSelectedPage] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Filtrer les pages en fonction de la recherche
  const filteredPages = docData.pages.filter(page =>
    page.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    page.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Navigation et recherche */}
      <div className="grid grid-cols-12 gap-6">
        {/* Sidebar */}
        <div className="col-span-3">
          <div className="sticky top-8 space-y-6">
            {/* Recherche */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher une page..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Liste des pages */}
            <nav className="space-y-1">
              {filteredPages.map((page) => (
                <button
                  key={page.id}
                  onClick={() => setSelectedPage(page)}
                  className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-lg ${
                    selectedPage?.id === page.id
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <FileText className="h-5 w-5 mr-3" />
                  {page.title}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Contenu principal */}
        <div className="col-span-9">
          {selectedPage ? (
            <PageDocumentation page={selectedPage} />
          ) : (
            <div className="text-center py-12">
              <BookOpen className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                Sélectionnez une page
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Choisissez une page dans le menu pour voir sa documentation détaillée
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentationFonctionnelle;